import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { SubSink } from 'subsink';

import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { BrandingService } from '../../services/branding.service';
import { PermissionService } from '../../services/permission.service';

import { KarveTab, TabZone } from './karve-tabview.types';

@Component({
  selector: 'app-karve-tabview',
  standalone: true,
  imports: [
    FreyaCommonModule
  ],
  templateUrl: './karve-tabview.component.html',
  styleUrls: ['./karve-tabview.component.scss',]
})
export class KarveTabviewComponent implements OnInit, OnDestroy {
  @Input() tabs: KarveTab[];
  @Input() baseUrl: string;

  visibleTabs: KarveTab[] = [];

  selectedTab: string;

  zone: string;

  private currentZone: TabZone | undefined;

  /** Slug of the tab clicked most recently — drives the optimistic highlight
   *  until the router confirms. routerLinkActive only adds .p-highlight after
   *  navigation completes (guards + route activation), so heavy switch targets
   *  would otherwise leave the stale tab highlighted while the next view
   *  loads. Cleared on route confirmation or after a 2s safety timeout. */
  private pendingSlug: string | null = null;
  private pendingTimeout: any = null;

  subs = new SubSink();
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private branding: BrandingService,
    private permissionService: PermissionService,
  ) { }

  ngOnInit(): void {
    this.recomputeVisibleTabs();
    this.syncSelectedTabFromUrl(this.router.url);
    this.watchRouteChange();
    this.watchZoneChange();
  }

  private recomputeVisibleTabs(): void {
    this.visibleTabs = this.tabs.filter((tab) =>
      (!tab.permission || this.permissionService.checkPermission(tab.permission))
      && (!tab.visibleInZone || (!!this.currentZone && tab.visibleInZone(this.currentZone))),
    );
  }

  /**
   * Tracks the confirmed active tab. The previous subscription was on
   * `route.firstChild?.url` — the child ActivatedRoute instance is REPLACED on
   * every tab switch, so the subscription died after the first navigation and
   * `selectedTab` (the dropdown's bound value) went stale; routerLinkActive
   * kept re-evaluating because it lives on the tab routeLink itself. Router
   * NavigationEnd events survive child swaps, and in every usage of this
   * component (schedule / people / job / jobs pages) the tab slug is the URL
   * segment right after `baseUrl`.
   */
  private watchRouteChange(): void {
    this.subs.sink = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects ?? event.url;
        this.syncSelectedTabFromUrl(url);
        // Route confirmed — drop the optimistic highlight handoff.
        this.clearPending();
      }
    });
  }

  /** Reads the active tab slug out of a router URL: the segment directly
   *  after `baseUrl`. No-op for urls outside this tabview. */
  private syncSelectedTabFromUrl(url: string): void {
    const path = (url || '').split('?')[0].replace(/\/+$/, '');
    const prefix = this.baseUrl + '/';
    if (!path.startsWith(prefix)) { return; }
    const tab = path.slice(prefix.length).split('/')[0];
    if (tab && (!this.selectedTab || tab !== this.selectedTab)) {
      this.selectedTab = tab;
    }
  }

  private clearPending(): void {
    this.pendingSlug = null;
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  private watchZoneChange(): void {
    this.subs.sink = this.branding.currentZone().subscribe((zone) => {
      // Fall back to '' (not undefined) so getUrl() never emits ?zone=undefined.
      // Keeps the param present (value empty) so `'zone' in params` watchers and
      // the zone guards behave exactly as before; both treat '' as "no zone".
      this.zone = zone?.id ?? '';
      this.currentZone = zone ?? undefined;
      this.recomputeVisibleTabs();
    });
  }

  public goToRoute(tab: any): void {
    this.router.navigate([this.baseUrl, tab.value]);
  }

  ngOnDestroy(): void {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
    }
    this.subs.unsubscribe();
  }

  getUrl(tab: typeof this.tabs[0]) {
    return `${this.baseUrl}/${tab.slug}?zone=${this.zone}`;
  }

  handleTabClick(event: MouseEvent, tab: typeof this.tabs[0]) {
    // Only prevent default if no modifier keys are pressed
    if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
      event.preventDefault();
      // Optimistic highlight (see pendingSlug note).
      this.pendingSlug = tab.slug;
      if (this.pendingTimeout) {
        clearTimeout(this.pendingTimeout);
      }
      this.pendingTimeout = setTimeout(() => this.clearPending(), 2000);
    }
  }
}
